import { Info } from '@mui/icons-material';
import {
  FormControl,
  Grid,
  ListSubheader,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { assignOwnerToConfirmingRequest } from 'lib/api/confirming';
import { ConfirmingRequestById } from 'lib/api/confirming/types';
import { Executive } from 'lib/api/factoring/types';
import { DocumentProfileContext } from 'lib/context/DocumentProfileProvider';
import CustomButton from 'lib/designSystem/Button';
import PageHeader from 'lib/designSystem/PageHeader';
import EnhancedTable from 'lib/designSystem/Table';
import { LightTooltip } from 'lib/designSystem/WhiteTooltip';
import { formatMoney, showAlert, validatePermissions } from 'lib/functions/utils';
import RequestAnalysts from 'pages/Finance/Components/Financing/common/requestAnalysts';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AuthState } from 'services/redux/authSlice';
import { showBusinessNetwork } from 'services/redux/businessNetworkSlice';

import { ConfirmingExportRequest } from '../../../../../common/SimulationExportDialog/types/ConfirmingExportRequest';
import ExportDialog from '../../../ExportDialog';
import OperationResume from '../../../Simulator/components/OperationResume';
import {
  calculateRequestDetailsEarlyPayment,
  calculateRequestDetailsTraditional,
} from '../../../Simulator/components/Simulator';
import SimulationEarlyPayment from '../../../Simulator/components/Simulator/components/SimulationEarlyPayment';
import SimulationTraditional from '../../../Simulator/components/Simulator/components/SimulationTraditional';
import {
  ConfirmingRequestForCreation,
  OperationDetailsEarlyPayment,
  OperationDetailsTraditional,
} from '../../../Simulator/types';
import { mapConfirmingRequestByIdToForCreation } from '../../../Simulator/utils/mapConfirmingRequestByIdToForCreation';
import ConfirmingRequestUpdateStatusModal from './components/UpdateStatusModal';
import { downloadAEC } from 'lib/api/reSell/factoring';

const gridStyle = {
  alignContent: 'center',
  alignItems: 'center',
  padding: '4px',
  paddingRight: 23,
};

const RENDER_ANALYSTS_STATUSES = ['En revisión', 'Aceptada', 'En giro'];

export interface UpdateStatusTransitions {
  [key: string]: { key: string; label: string }[];
}

const CustomTooltip = (text: Array<Array<string>>) => (
  <div style={{ padding: 10 }}>
    <div style={{ display: 'flex', alignItems: 'center', paddingBottom: 10 }}>
      <Typography variant="body1">Descripción</Typography>
    </div>
    <Table size="small">
      <TableBody>
        {text.map((row) => (
          <TableRow key={row[0]}>
            <TableCell key={row[0]}>{row[0]}</TableCell>
            <TableCell key={row[1]}>{row[1]}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </div>
);

type SummaryModuleProps = {
  selectedRequest: ConfirmingRequestById;
  setOpen: (open: boolean) => void;
  handleEdit: (id: number) => Promise<void>;
  parentSetUp: (id?: number) => Promise<void>;
  refreshOpenRequest: (id: number) => Promise<void>;
  executivesPortfolio: Executive[];
  executivesAll: Executive[];
};

const SummaryModule = (props: SummaryModuleProps) => {
  const {
    selectedRequest,
    setOpen,
    handleEdit,
    parentSetUp,
    refreshOpenRequest,
    executivesPortfolio,
    executivesAll,
  } = props;
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  const dispatch = useDispatch();
  const [operationDetailsTraditional, setOperationDetailsTraditional] =
    useState<OperationDetailsTraditional>({
      operationAmount: 0,
      operationAdvanced: 0,
      operationCostRate: 0,
      operationToDeposit: 0,
      operationAccountReceivable: 0,
      operationIva: 0,
      expensesCost: 0,
      expensesIva: 0,
      operationCostComissionFixed: 0,
      operationCostComissionVariable: 0,
      advance: 0,
      rate: 0,
      fundRate: 0,
      documentsWithDetails: [],
    });
  const [operationDetailsEarlyPayment, setOperationDetailsEarlyPayment] =
    useState<OperationDetailsEarlyPayment>({
      operationAmount: 0,
      operationAdvanced: 0,
      operationCostRate: 0,
      operationToDeposit: 0,
      operationIva: 0,
      expensesCost: 0,
      expensesIva: 0,
      operationCostComissionFixed: 0,
      operationCostComissionVariable: 0,
      advance: 0,
      rate: 0,
      fundRate: 0,
      documentsWithDetails: [],
    });
  const [openUpdateStatusModal, setOpenUpdateStatusModal] = useState(false);
  const [openExportDialog, setOpenExportDialog] = useState(false);
  const [selectedExecutive, setSelectedExecutive] = useState<string | number>(
    '',
  );

  const canEditExecutive = validatePermissions(
    user,
    'financing_confirming_edit',
  );
  const getCurrentExecutiveId = () =>
    selectedRequest?.userOwner?.id ?? selectedRequest?.userOwnerId;
  ('');
  const assignOwnerMutation = assignOwnerToConfirmingRequest(dispatch, {
    onSuccess: (requestId) => void refreshOpenRequest?.(requestId),
    onError: () => setSelectedExecutive(getCurrentExecutiveId() || ''),
  });

  const { onOpenDocumentProfile } = useContext(DocumentProfileContext);

  const updateStatusTransitions: UpdateStatusTransitions = {
    Aceptada: [
      {
        key: 'IN_REVIEW',
        label: 'En revisión',
      },
    ],
    'En giro': [
      ...(selectedRequest.comiteeOperationsId
        ? [
            {
              key: 'ACCEPTED',
              label: 'Aceptada',
            },
          ]
        : [
            {
              key: 'IN_REVIEW',
              label: 'En revisión',
            },
          ]),
    ],
  };

  const express = useSelector(
    (state: { auth: AuthState }) => state.auth.user?.express,
  );

  // Find executive name by ID for display
  const getExecutiveName = (executiveId: string | number) => {
    if (!executiveId) return '';

    // Search in portfolio executives first
    const portfolioExecutive = executivesPortfolio?.find((exec) => {
      return (
        exec.id === executiveId ||
        exec.id === Number(executiveId) ||
        String(exec.id) === executiveId
      );
    });
    if (portfolioExecutive) {
      return portfolioExecutive.name;
    }

    // Search in all executives
    const allExecutive = executivesAll?.find((exec) => {
      return (
        exec.id === executiveId ||
        exec.id === Number(executiveId) ||
        String(exec.id) === executiveId
      );
    });
    if (allExecutive) {
      return allExecutive.name;
    }

    return '';
  };

  // Sync selected executive from request
  useEffect(() => {
    const executiveId =
      selectedRequest?.userOwner?.id ?? selectedRequest?.userOwnerId;
    setSelectedExecutive(executiveId || '');
  }, [
    selectedRequest?.id,
    selectedRequest?.userOwner?.id,
    selectedRequest?.userOwnerId,
  ]);

  const requestForCreation: ConfirmingRequestForCreation = useMemo(
    () => mapConfirmingRequestByIdToForCreation(selectedRequest),
    [selectedRequest],
  );

  const handleExecutiveChange = (newOwnerId: string | number) => {
    if (!newOwnerId || !selectedRequest?.id) return;
    setSelectedExecutive(newOwnerId);
    assignOwnerMutation.mutate({
      requestId: selectedRequest.id,
      ownerId: Number(newOwnerId),
    });
  };

  const canEditRequest = () => {
    if (
      !updateStatusTransitions[selectedRequest.status] ||
      updateStatusTransitions[selectedRequest.status].length === 0
    )
      return false;
    if (
      !validatePermissions(user, 'financing_confirming_edit_operation_status')
    )
      return false;
    return true;
  };

  const handleExportSimulation = async () => {
    setOpenExportDialog(true);
  };

  const handleShowBusinessDetails = (id: string) => {
    const row = selectedRequest?.documents?.find((x) => x.id === id);
    if (!row) return;
    dispatch(
      showBusinessNetwork({
        id: row.businessIdentifier,
        name: row.businessName,
      }),
    );
  };
  const downloadFile = (signedUrl: string, index: number) => {
    setTimeout(() => {
      const link = document.createElement('a');
      link.href = signedUrl;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }, index * 250);
  };


  const handleDownloadAEC = async (id: string) => {
    try {
      const response = await downloadAEC(dispatch, [id]);
      const documentAEC = response[0];
      if (documentAEC.xml) {
        downloadFile(documentAEC.xml, documentAEC.xmlFileName);
        showAlert(dispatch, 'success', 'AEC descargado correctamente');
      } else {
        showAlert(
          dispatch,
          'error',
          'El archivo AEC no se encuentra disponible',
        );
      }
    } catch (error) {
      showAlert(dispatch, 'error', 'Error inesperado');
      console.error(error);
    }
  };


  const canShowDocumentProfile =
    selectedRequest.status === 'Aceptada' ||
    selectedRequest.status === 'Activa' ||
    selectedRequest.status === 'Completada';

  useEffect(() => {
    if (requestForCreation) {
      if (requestForCreation.productType === 'traditional') {
        setOperationDetailsTraditional(
          calculateRequestDetailsTraditional(requestForCreation),
        );
      } else if (requestForCreation.productType === 'earlyPayment') {
        setOperationDetailsEarlyPayment(
          calculateRequestDetailsEarlyPayment(requestForCreation),
        );
      }
    }
  }, [requestForCreation]);

  const renderAnalysts =
    RENDER_ANALYSTS_STATUSES.includes(selectedRequest.status) &&
    selectedRequest.id;

  const tableData = useMemo(() => {
    if (operationDetailsTraditional.documentsWithDetails.length > 0) {
      return operationDetailsTraditional.documentsWithDetails;
    }
    if (operationDetailsEarlyPayment.documentsWithDetails.length > 0) {
      return operationDetailsEarlyPayment.documentsWithDetails;
    }
    return selectedRequest.documents;
  }, [
    operationDetailsTraditional.documentsWithDetails,
    operationDetailsEarlyPayment.documentsWithDetails,
    selectedRequest.documents,
  ]);
  const showDownloadAEC = !user?.express;

  return (
    <div>
      <Grid container>
        <Grid item xs={7}>
          <div
            style={{
              paddingRight: '10px',
              borderRight: '1px solid rgba(0, 0, 0, 0.12)',
            }}
          >
            <OperationResume
              stakeholderName={selectedRequest.stakeholderName}
              stakeholderIdentifier={selectedRequest.stakeholderIdentifier}
              productType={selectedRequest.type}
              requestDate={selectedRequest.operationDate}
            />
            <div style={{ padding: '20px 0px' }}>
              <Grid
                container
                style={gridStyle}
                columnSpacing={3}
                rowSpacing={1}
              >
                <Grid item xs={2.9}>
                  <Typography fontWeight={600}>Ejecutivo</Typography>
                </Grid>
                <Grid item xs={2.9}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <Typography fontWeight={600}>Montos</Typography>
                    <LightTooltip
                      title={CustomTooltip([
                        [
                          'Anticipo',
                          'Porcentaje a financiar respecto al total del valor de la operación.',
                        ],
                      ])}
                    >
                      <Info
                        style={{
                          marginLeft: 6,
                          fontSize: 14,
                        }}
                      />
                    </LightTooltip>
                  </div>
                </Grid>
                <Grid item xs={6.2}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Typography fontWeight={600}>Tasas</Typography>
                    <LightTooltip
                      title={CustomTooltip([
                        [
                          'Interés',
                          'Interés aplicado a la operación. Se descuenta del monto a financiar como diferencia de precio.',
                        ],
                        [
                          'Interés mora',
                          'Interés aplicado por cada día de mora en incrementos diarios.',
                        ],
                      ])}
                    >
                      <Info style={{ marginLeft: 6, fontSize: 14 }} />
                    </LightTooltip>
                  </div>
                </Grid>
                <Grid item xs={2.9}>
                  <Typography variant="body1">Seleccionar ejecutivo</Typography>
                  <FormControl fullWidth>
                    <Select
                      value={selectedExecutive}
                      disabled={
                        !canEditExecutive || assignOwnerMutation.isPending
                      }
                      onChange={(e) => {
                        const v = e.target.value;
                        handleExecutiveChange(v);
                      }}
                      displayEmpty
                      renderValue={(value) => {
                        if (value) {
                          const name = getExecutiveName(value);
                          return name || 'Seleccionar ejecutivo';
                        }
                        return 'Seleccionar ejecutivo';
                      }}
                    >
                      <ListSubheader sx={{ fontWeight: 'bold' }}>
                        Cliente en cartera
                      </ListSubheader>
                      {executivesPortfolio?.map((executive) => (
                        <MenuItem key={executive.id} value={executive.id}>
                          {executive.name}
                        </MenuItem>
                      ))}
                      <ListSubheader sx={{ fontWeight: 'bold' }}>
                        Todos
                      </ListSubheader>
                      {executivesAll?.map((executive) => (
                        <MenuItem key={executive.id} value={executive.id}>
                          {executive.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={2.9}>
                  <Typography variant="body1">Anticipo (%) </Typography>
                  <TextField
                    value={selectedRequest.advance}
                    placeholder="100"
                    type="number"
                    fullWidth
                    disabled={true}
                    style={{ padding: '0px !important' }}
                  />
                </Grid>
                <Grid item xs={1.7}>
                  <Typography variant="body1">Interés (%)</Typography>
                  <TextField
                    value={selectedRequest.rate}
                    type="number"
                    fullWidth
                    placeholder="2.2"
                    disabled={true}
                  />
                </Grid>
                <Grid item xs={2.1}>
                  <Typography variant="body1">Fondo (%)</Typography>
                  <TextField
                    value={selectedRequest.fundRate}
                    type="number"
                    fullWidth
                    placeholder="0"
                    disabled={true}
                  />
                </Grid>
                <Grid item xs={2.4}>
                  <Typography variant="body1">Interés mora (%)</Typography>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <TextField
                      value={
                        selectedRequest.arrearType === 'table'
                          ? 'Tabla'
                          : selectedRequest.arrearRate
                      }
                      type={
                        selectedRequest.arrearType === 'table'
                          ? 'text'
                          : 'number'
                      }
                      fullWidth
                      disabled={true}
                      placeholder="2.2"
                    />
                  </div>
                </Grid>
                <Grid item xs={5.8}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginTop: 10,
                    }}
                  >
                    <Typography variant="body1" fontWeight={600}>
                      Comisión operación
                    </Typography>
                    <LightTooltip
                      title={CustomTooltip([
                        [
                          'Fijo',
                          'Monto fijo a cobrar por la operación. Reducirá el monto a financiar',
                        ],
                        [
                          'Variable',
                          'Porcentaje a cobrar por la operación. Reducirá el monto a financiar',
                        ],
                      ])}
                    >
                      <Info style={{ marginLeft: 6, fontSize: 14 }} />
                    </LightTooltip>
                  </div>
                  <Grid container columnSpacing={3}>
                    <Grid item xs={6}>
                      <Typography variant="body1">Fijo ($)</Typography>
                      <TextField
                        value={formatMoney(
                          selectedRequest.operationCostFixed || 0,
                        )}
                        type="text"
                        fullWidth
                        disabled={true}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body1">Variable (%)</Typography>
                      <TextField
                        value={selectedRequest.operationCostVariablePercentage}
                        type="number"
                        fullWidth
                        disabled={true}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={3.1}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginTop: 10,
                    }}
                  >
                    <Typography fontWeight={600}>Otros gastos</Typography>
                    <LightTooltip
                      title={CustomTooltip([
                        [
                          'Fijo',
                          'Valor de cualquier otro gasto en el que se incurra en la operación',
                        ],
                      ])}
                    >
                      <Info style={{ marginLeft: 6, fontSize: 14 }} />
                    </LightTooltip>
                  </div>
                  <Typography variant="body1">Fijo ($)</Typography>
                  <TextField
                    value={formatMoney(selectedRequest.expensesCost)}
                    type="text"
                    fullWidth
                    disabled={true}
                  />
                </Grid>
                <Grid item xs={3.1}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginTop: 10,
                    }}
                  >
                    <Typography fontWeight={600}>Canje</Typography>
                    <LightTooltip
                      title={CustomTooltip([
                        [
                          'Dás de canje',
                          'Días a sumar para el cálculo de la diferencia de precio',
                        ],
                      ])}
                    >
                      <Info style={{ marginLeft: 6, fontSize: 14 }} />
                    </LightTooltip>
                  </div>
                  <Typography variant="body1">Días de canje</Typography>
                  <TextField
                    value={selectedRequest.exchangeDays}
                    type="number"
                    fullWidth
                    disabled={true}
                  />
                </Grid>
              </Grid>
            </div>
            {renderAnalysts && (
              <RequestAnalysts
                requestId={selectedRequest.id}
                requestType="confirming"
                sx={{ mr: 2.5 }}
              />
            )}
          </div>
        </Grid>
        <Grid item xs={5}>
          {selectedRequest.type === 'traditional' && (
            <SimulationTraditional
              expanded={true}
              onAccordionChange={() => () => {}}
              requestForCreation={requestForCreation}
              operationDetails={operationDetailsTraditional}
              onExportSimulation={handleExportSimulation}
            />
          )}
          {selectedRequest.type === 'earlyPayment' && (
            <SimulationEarlyPayment
              expanded={true}
              onAccordionChange={() => () => {}}
              requestForCreation={requestForCreation}
              operationDetails={operationDetailsEarlyPayment}
              onExportSimulation={handleExportSimulation}
            />
          )}
        </Grid>
      </Grid>
      <PageHeader.TitleOnly
        title="Detalle documentos"
        subtitle={`${selectedRequest.documents.length} documento ${selectedRequest.documents.length === 1 ? 'seleccionado' : 's seleccionados'}`}
        variant="subHeader"
        iconType="documents"
      />
      <EnhancedTable
        dataHeaders={[
          ...(showDownloadAEC
            ? [
                {
                  key: 'downloadAEC',
                  label: '',
                  handleAction: handleDownloadAEC,
                  icon: <Typography fontWeight={800}>AEC</Typography>,
                },
              ]
            : []),
          ...(canShowDocumentProfile
            ? [
                {
                  key: 'folio',
                  label: 'Folio',
                  handleAction: onOpenDocumentProfile,
                  icon: <Info fontSize="small" />,
                },
              ]
            : [
                {
                  key: 'folio',
                  label: 'Folio',
                },
              ]),
          ...(selectedRequest.status === 'Activa' ||
          selectedRequest.status === 'Completada'
            ? [
                {
                  key: 'type',
                  label: 'Tipo',
                },
                {
                  key: 'paidAt',
                  label: 'Pagado',
                  type: 'paid-lights',
                },
                {
                  key: 'creditNotesDate',
                  label: 'NC',
                  type: 'cancel-lights',
                  canNull: true,
                },
                {
                  key: 'cedded',
                  label: 'Cedida',
                  type: 'cession-lights',
                },
                {
                  key: 'cancelDate',
                  label: 'Reclamos',
                  type: 'cancel-lights',
                  canNull: true,
                },
                {
                  key: 'confirmDate',
                  label: 'Confirmada',
                  type: 'confirm-lights',
                  canNull: true,
                },
              ]
            : []),
          {
            key: 'cedded',
            label: 'Cedida',
            type: 'cession-lights',
          },
          {
            key: 'cancelDate',
            label: 'Reclamos',
            type: 'cancel-lights',
            canNull: true,
          },
          {
            key: 'confirmDate',
            label: 'Acuse',
            type: 'confirm-lights',
            canNull: true,
          },
          {
            key: 'creditNoteAmount',
            label: 'NC',
            type: 'money-colored',
          },
          {
            key: 'subType',
            label: 'Código Doc.',
          },
          {
            key: 'businessIdentifier',
            label: 'Rut Proveedor',
          },
          {
            key: 'businessName',
            label: 'Proveedor',
            ...(!express ? { handleAction: handleShowBusinessDetails } : {}),
            ...(!express ? { icon: <Info fontSize="small" /> } : {}),
          },
          {
            key: 'issuedDate',
            label: 'Emisión',
            type: 'date',
          },
          {
            key: 'receiptDate',
            label: 'Recepción',
            type: 'eight-days-date',
          },
          {
            key: 'amount',
            label: 'Monto Doc.',
            type: 'money',
          },
          {
            key: 'costRate',
            label: 'Dif. precio',
            type: 'money',
          },
          ...(selectedRequest.type === 'earlyPayment'
            ? [
                {
                  key: 'costExpenses',
                  label: 'Gastos',
                  type: 'money',
                },
                {
                  key: 'costComission',
                  label: 'Comisión',
                  type: 'money',
                },
                {
                  key: 'amountToDeposit',
                  label: 'A girar',
                  type: 'money',
                },
              ]
            : []),
          {
            key: 'paymentDateShortString',
            label: 'Vencimiento',
          },
          {
            key: 'daysToCount',
            label: 'Días',
          },
        ]}
        data={tableData}
        rowsPerPageDefault={5}
      />
      <div
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '10px 0px',
          gap: '10px',
        }}
      >
        <div>
          {canEditRequest() && (
            <Typography
              color="orange"
              variant="body1"
              fontWeight="bold"
              style={{ cursor: 'pointer' }}
              onClick={() => setOpenUpdateStatusModal(true)}
            >
              Editar Estado
            </Typography>
          )}
        </div>
        <div
          style={{
            marginTop: 10,
            paddingBottom: 10,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 10,
          }}
        >
          <CustomButton
            color="secondary"
            onClick={() => {
              setOpen(false);
            }}
          >
            Salir
          </CustomButton>
          {selectedRequest.status === 'En revisión' && (
            <CustomButton
              color="primary"
              onClick={() => {
                void handleEdit(selectedRequest.id);
                setOpen(false);
              }}
              disabled={!validatePermissions(user, 'financing_confirming_edit')}
            >
              Editar
            </CustomButton>
          )}
        </div>
      </div>
      {openUpdateStatusModal && (
        <ConfirmingRequestUpdateStatusModal
          updateStatusTransitions={updateStatusTransitions}
          open={openUpdateStatusModal}
          setOpen={setOpenUpdateStatusModal}
          requestId={selectedRequest.id}
          setUp={parentSetUp}
          oldStatus={selectedRequest.status}
          selectedId={String(selectedRequest.id)}
        />
      )}
      <ExportDialog
        open={openExportDialog}
        setOpen={setOpenExportDialog}
        selectedRequest={
          {
            ...selectedRequest,
            operationToDeposit:
              selectedRequest.type === 'traditional'
                ? operationDetailsTraditional.operationToDeposit
                : operationDetailsEarlyPayment.operationToDeposit,
          } as unknown as ConfirmingExportRequest
        }
      />
    </div>
  );
};

export default SummaryModule;
